import { ChangeDetectorRef, Component, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, finalize, skip, takeUntil } from 'rxjs/operators';
import { EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { SharedservicesService } from '@app-shared/services/sharedService/sharedservices.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import {
  AssignmentResponse,
  Coach,
  CoachAssignmentRequest,
  CoachingData,
  CoachListParams,
  CoachResponse,
  SearchHistory,
} from '@app-shared/common/coach-assignment.model';
import { RELEASE_DATE } from '@app-shared/common/batch-update.constants';
import { DataService } from '@app-core/services/data/data.service';
import { Driver, UnifiedDriverDetails } from '@app-driver-management/common/driver-management.model';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Component({
  selector: 'app-assign-coach',
  templateUrl: './assign-coach.component.html',
  styleUrls: ['./assign-coach.component.scss'],
})
export class AssignCoachComponent implements OnInit {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public coachList: Coach[] = [];
  @Input() loader: boolean = false;
  @ViewChild(MatAutocompleteTrigger) trigger!: MatAutocompleteTrigger;
  @Input() label: string;
  @Input() showLabel: boolean = true;
  @Input() appearance: string = '';
  @Input() table: boolean;
  @Input() driver: UnifiedDriverDetails;
  public coachControl = new FormControl('');
  public hideLabel: boolean = false;
  public tagIdsFromDriverTags: number[];
  public coachId: string;
  public driverId: string;
  public assignedCoachId: string;
  public selectedDriverId: string;
  @Input() context!: string | null; // Receiving context from parent
  context$ = new BehaviorSubject<string | null>(null); // Reactive context
  @Output()
  public doRefresh = new EventEmitter<void>();
  @Input() autoCompleteOpen: Subject<Driver>;
  @Output() closeAutoComplete = new EventEmitter<UnifiedDriverDetails>();
  @ViewChild('auto') matAuto!: MatAutocomplete;
  @Output()
  public doRefreshForCochableDriverList = new EventEmitter<void>();
  @Output() coachSelected = new EventEmitter<string>();
  @Input() tagListFromEditAddDriver: number[];
  @Input() AssignSingleCoachToMultipleDriver: boolean;
  @Input() driverIds: string;
  @Input() driverTags: number[];
  @Input() driverForTempCoach: CoachingData;
  public assignedTempCoachId: string;
  @Output() openModalWindow: EventEmitter<any> = new EventEmitter<any>();
  public assignedPermanentCoachId: string;
  public recentSearches: Array<SearchHistory> = [];
  private readonly SEARCH_COOKIE_KEY: string = 'recent_coaches';
  @Input() assignCoachValue: string;
  @Input() isHovered: boolean = false;
  @Output() openBulkConfirmWindow: EventEmitter<[boolean, string]> = new EventEmitter<[boolean, string]>();
  public driverIdsArrayCount: string;
  public fleetId: string;
  private previousFleetId: string | null = null;
  constructor(
    private sharedService: SharedservicesService,
    private translate: TranslateService,
    private snackbarService: SnackBarService,
    private cookieService: CookieService,
    public dataService: DataService,
    private cdRef: ChangeDetectorRef
  ) {}
  ngOnInit() {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((fleetId) => {
      if (this.previousFleetId && this.previousFleetId !== fleetId) {
        this.clearRecentSearches();
      }

      this.previousFleetId = fleetId;
    });
    if (this.cookieService.check(this.SEARCH_COOKIE_KEY)) {
      this.recentSearches = this.getRecentSearches();
    }
    this.checkIfNew();

    if (this.context === 'list') {
      if (this.driver?.assigned_coach?.coachAssigned) {
        this.coachControl.setValue(this.driver.assigned_coach.coachAssigned.coachName);
      }
    } else {
      if (this.driverForTempCoach?.assignedCoach?.tempCoachAssigned?.coachName) {
        this.coachControl.setValue(this.driverForTempCoach?.assignedCoach?.tempCoachAssigned?.coachName);
      } else {
        this.coachControl.setValue(this.driverForTempCoach?.assignedCoach?.coachAssigned?.coachName);
      }
    }
    this.coachControl.valueChanges
      .pipe(
        skip(1),
        distinctUntilChanged(), // Ensure API is only called when input changes
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((searchQuery: string) => {
        const tagData = this.tagIdsFromDriverTags ?? this.driverTags ?? this.tagListFromEditAddDriver;

        this.getCoachList(false, searchQuery, tagData);
      });
  }
  public clearRecentSearches(): void {
    this.cookieService.delete(this.SEARCH_COOKIE_KEY);
    this.recentSearches = [];
  }
  public storeSearch(coach) {
    let searches = this.getRecentSearches();

    // Prevent duplicates
    const newSearch = {
      name: coach.name,
      role: coach.fleets[0]?.role?.roleName,
      coachId: coach.userId,
    };
    searches = searches.filter((search) => !(search.name === newSearch.name && search.role === newSearch.role));

    // Add the new search to the beginning
    searches.unshift(newSearch);

    // Keep only the latest 3 searches
    if (searches.length > 3) {
      searches.pop();
    }

    // Store in cookies
    this.cookieService.set(this.SEARCH_COOKIE_KEY, JSON.stringify(searches));
    this.recentSearches = searches;
  }
  public getRecentSearches(): { name: string; role: string; coachId: string }[] {
    const storedSearches = this.cookieService.get(this.SEARCH_COOKIE_KEY);
    return storedSearches ? JSON.parse(storedSearches) : [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['autoCompleteOpen'] && this.autoCompleteOpen) {
      this.autoCompleteOpen.subscribe((val) => {
        if (val.driverId != this.driver?.driverId) {
          this.trigger?.closePanel();
        }
      });
    }
    if (changes['driverIds']) {
      this.driverIdsArrayCount = changes['driverIds'].currentValue;

      if (Array.isArray(this.driverIdsArrayCount)) {
        this.driverIds = this.driverIdsArrayCount.join(',');
      }
    }
    if (changes['driverTags']) {
      const updatedTagsArray = changes['driverTags'].currentValue;

      if (Array.isArray(updatedTagsArray)) {
        // Flatten the nested arrays and remove duplicates
        this.driverTags = [...new Set([].concat(...updatedTagsArray))];
      }
    }
    if (changes.context && changes.context.currentValue) {
      this.context$.next(changes.context.currentValue); // Update BehaviorSubject
    }
    if (changes.assignCoachValue) {
      this.coachControl.setValue(this.assignCoachValue);
    }
  }

  private checkIfNew() {
    const release = new Date(RELEASE_DATE);
    const threeMonthsLater = new Date(release);
    threeMonthsLater.setMonth(release.getMonth() + 3);

    this.showLabel = new Date() < threeMonthsLater;
  }

  public selectCoach(coach: Coach) {
    this.hideLabel = false;
    this.coachControl.setValue(coach.name);
    this.storeSearch(coach);
    this.coachId = coach.userId;
    this.cdRef.detectChanges();
    this.coachSelected.emit(this.coachId);
    // Ensure execution happens only when context has a value
    this.context$.subscribe((context) => {
      if (!context) {
        return;
      }

      switch (context) {
        case 'list':
          this.permanentCoachAssignment(this.coachId, this.driver.driverId);
          break;
        case 'assignSingleCoachToMultipleDriver':
          if (this.driverIdsArrayCount.length > 5) {
            this.openBulkConfirmWindow.emit([true, coach.name]);
          } else {
            this.permanentCoachAssignment(this.coachId, this.driverIds);
            break;
          }
        case 'temporary':
          this.openModalWindow.emit({
            CoachId: coach.userId,
            DriverId: this.driverForTempCoach?.driverId,
          });
          break;
      }
    });
  }
  public BulkCoachAssignment() {
    this.permanentCoachAssignment(this.coachId, this.driverIds);
  }
  public handleClick() {
    this.closeAutoComplete.next(this.driver);
    if (!this.table) {
      if (this.AssignSingleCoachToMultipleDriver) {
        this.getCoachList(true, '', this.driverTags);
      } else {
        this.getCoachList(true, '', this.tagListFromEditAddDriver);
      }
    } else {
      if (this.context === 'temporary') {
        this.assignedTempCoachId = this.driverForTempCoach?.assignedCoach?.tempCoachAssigned?.coachId;
        this.assignedPermanentCoachId = this.driverForTempCoach?.assignedCoach?.coachAssigned?.coachId;
        this.getCoachList(true);
      } else {
        this.hideLabel = true;
        this.assignedCoachId = this.driver.assigned_coach?.coachAssigned?.coachId;
        this.selectedDriverId = this.driver.driverId;
        this.tagIdsFromDriverTags = this.driver?.driverTags?.map((tag) => tag.tagId);
        this.getCoachList(true, '', this.tagIdsFromDriverTags);
      }
    }
  }
  public assignCoachFromRecentSearch(search) {
    this.coachSelected.emit(search.coachId);
    const IDS = this.selectedDriverId ?? this.driverIds ?? this.driverForTempCoach?.driverId;
    if (this.context != 'EditAdd') {
      if (this.context == 'temporary') {
        this.openModalWindow.emit({
          CoachId: search.coachId,
          DriverId: this.driverForTempCoach?.driverId,
        });
      } else {
        this.permanentCoachAssignment(search.coachId, IDS);
      }
    }
  }
  public handleBlur() {
    // Show label only if input is empty
    if (!this.coachControl.value) {
      this.hideLabel = false;
    }
  }

  public isCoachAssigned(coach: Coach): boolean {
    if (!this.assignedCoachId) {
      return false; // If assignedCoachId is not set, return false immediately
    }
    return this.assignedCoachId === coach.userId;
  }
  public isTempCoachAssigned(coach: Coach): boolean {
    if (!this.assignedTempCoachId) {
      return false;
    }
    return this.assignedTempCoachId === coach.userId;
  }
  public isPermanentCoachAssigned(coach: Coach): boolean {
    if (!this.assignedPermanentCoachId) {
      return false;
    }
    return this.assignedPermanentCoachId === coach.userId;
  }

  public permanentCoachAssignment(coachId, driverId) {
    const driverIdsArray = typeof driverId === 'string' ? driverId.split(',') : [this.driverIds];

    const body: CoachAssignmentRequest = {
      assignments: [
        {
          coachId: coachId,
          driverIds: driverIdsArray,
        },
      ],
      unassignments: [],
    };
    this.sharedService.permanentCoachAssignment(body).subscribe(
      (res: AssignmentResponse) => {
        if (res.data?.assignedCount > 0) {
          this.snackbarService.success(this.translate.instant('CoachAssignSuccessfully'));
          if (this.context === 'temporary') {
            this.doRefreshForCochableDriverList.emit();
            return;
          } else {
            this.doRefresh.emit();
            return;
          }
        }
        if (res.data?.errors[0]?.reason === 'Tag Mismatch') {
          this.snackbarService.success(this.translate.instant('CoachAssignmentFailedTagsMismatch'));
          if (this.context === 'temporary') {
            this.doRefreshForCochableDriverList.emit();
          } else {
            this.doRefresh.emit();
          }
        } else {
          this.doRefresh.emit();
          this.snackbarService.success(this.translate.instant('CoachAssignFailed'));
        }
      },
      () => {}
    );
  }
  public permanentCoachUnassignment(coach: Coach) {
    const body: CoachAssignmentRequest = {
      assignments: [],
      unassignments: [
        {
          coachId: coach.userId,
          driverIds: [this.selectedDriverId || this.driverForTempCoach.driverId],
        },
      ],
    };
    this.sharedService.permanentCoachAssignment(body).subscribe(
      (res: AssignmentResponse) => {
        if (res.data?.unassignedCount === 1) {
          this.snackbarService.success(this.translate.instant('CoachUnAssignSuccessfully'));
          if (this.context === 'temporary') {
            this.doRefreshForCochableDriverList.emit();
            return;
          } else {
            this.doRefresh.emit();
            return;
          }
        }
        if (res.data?.errors?.length > 0) {
          this.snackbarService.success(this.translate.instant('CoachAssignFailed'));
          if (this.context === 'temporary') {
            this.doRefreshForCochableDriverList.emit();
          } else {
            this.doRefresh.emit();
          }
        }
      },
      () => {}
    );
  }
  public getCoachList(isRefresh?: boolean, searchQuery?: string, tagIds?: number[]) {
    const params: CoachListParams = {};

    // Add searchQuery if it has a value
    if (searchQuery) {
      params.search = searchQuery;
    }

    // Filter userTags and add only if it has values
    const userTags = Array.isArray(tagIds) ? tagIds.filter((tag) => tag) : [];
    if (userTags.length > 0) {
      params['userTags[]'] = userTags;
    }

    this.sharedService
      .getCoachList(params, isRefresh)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: CoachResponse) => {
          const { data = [] } = res || {};
          this.coachList = data;
        },
        () => {
          this.coachList = [];
        }
      );
  }

  public temporaryCoachAssignment(coachId, driverId) {
    const driverIdsArray = typeof driverId === 'string' ? driverId.split(',') : [this.driverIds];

    const body: CoachAssignmentRequest = {
      assignments: [
        {
          coachId: coachId,
          driverIds: driverIdsArray,
          isTempCoach: true,
        },
      ],
      unassignments: [],
    };
    this.sharedService.permanentCoachAssignment(body).subscribe(
      (res: AssignmentResponse) => {
        if (res.data?.assignedCount === 1) {
          this.snackbarService.success(this.translate.instant('CoachAssignSuccessfully'));
          this.doRefreshForCochableDriverList.emit();
          return;
        }
        if (res.data?.errors[0]?.reason === 'Tag Mismatch') {
          this.snackbarService.success(this.translate.instant('CoachAssignmentFailedTagsMismatch'));
          this.doRefreshForCochableDriverList.emit();
          return;
        } else {
          this.snackbarService.success(this.translate.instant('CoachAssignFailed'));
        }
      },
      () => {}
    );
  }
  public temporaryCoachUnassignment(coach: Coach) {
    const body: CoachAssignmentRequest = {
      assignments: [],
      unassignments: [
        {
          coachId: coach.userId,
          driverIds: [this.driverForTempCoach.driverId],
          isTempCoach: true,
        },
      ],
    };
    this.sharedService.permanentCoachAssignment(body).subscribe(
      (res: AssignmentResponse) => {
        if (res.data?.unassignedCount === 1) {
          this.snackbarService.success(this.translate.instant('CoachUnAssignSuccessfully'));
          this.doRefreshForCochableDriverList.emit();
          return;
        }
        if (res.data?.errors?.length > 0) {
          this.snackbarService.success(this.translate.instant('CoachAssignFailed'));
        }
      },
      () => {}
    );
  }
}
